import React, { useEffect, useState, useCallback } from 'react';
import { FiBell, FiX } from 'react-icons/fi';
import type { NotificationDTO } from '@astra/shared';
import { networkManager } from '../../systems/NetworkManager';
import { PALETTE } from '../station/stationTheme';

/**
 * Global notification bell — usable anywhere in space. Surfaces corp creation
 * contracts, corp membership applications, and asset transfer offers pushed
 * from the server (see corp.md), with Accept / Reject actions. The list opens
 * as a right-side slide-in drawer, matching the ShipInventoryModal /
 * CorporationHUD pattern.
 */
export function NotificationBell() {
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Animation state — `visible` lags `open` by one frame so the slide-in
  // transition can play. The drawer is always mounted; pointer events are
  // gated on `open` so it doesn't intercept clicks to the game while closed.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    return undefined;
  }, [open]);

  const refresh = useCallback(() => {
    const room: any = (networkManager as any).room;
    room?.send('getNotifications', {});
  }, []);

  // 'N' toggles the notifications drawer (ignored while typing).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.repeat) return;
      if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        setOpen((o) => {
          if (!o) refresh();
          return !o;
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [refresh]);

  useEffect(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;

    room.onMessage('notificationList', (msg: { items: NotificationDTO[] }) => {
      setItems(msg.items ?? []);
    });
    room.onMessage('notification', (n: NotificationDTO) => {
      setItems((prev) => [n, ...prev.filter((p) => p.id !== n.id)]);
    });
    // After acting, the server marks the related notifications "actioned"; refetch
    // to drop them. On failure, surface why (e.g. you can't afford the contribution).
    const onResult = (label: string) => (msg: any) => {
      setBusyId(null);
      if (msg && msg.success === false) setError(friendlyError(msg.error));
      else setError(null);
      refresh();
      void label;
    };
    room.onMessage('respondCorporationResult', onResult('corp'));
    room.onMessage('respondTransferOfferResult', onResult('transfer'));
    room.onMessage('respondCorpApplicationResult', onResult('application'));

    refresh();
    return () => { /* Colyseus handlers persist for the room lifetime */ };
  }, [refresh]);

  // ESC closes the drawer when open (captured so chat / constellation-map hotkeys don't also fire).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [open]);

  const act = (n: NotificationDTO, decision: 'accept' | 'reject') => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    setBusyId(n.id);
    setError(null);
    if (n.type === 'corp_contract') {
      room.send('respondToCorporationContract', { contractId: (n.payload as any).contractId, decision });
    } else if (n.type === 'transfer_offer') {
      room.send('respondToTransferOffer', { offerId: (n.payload as any).offerId, decision });
    } else if (n.type === 'corp_application') {
      const p = n.payload as any;
      room.send('respondCorpApplication', { corpId: p.corpId, applicantName: p.applicantName, decision });
    }
  };

  // Dismiss an info-only notification (no Accept/Reject — just clears it from
  // the bell by marking it read on the server). The existing
  // markNotificationRead message handler does the persistence; we refresh
  // locally so the item leaves the list without waiting for a server push.
  const dismiss = (n: NotificationDTO) => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    setBusyId(n.id);
    setError(null);
    room.send('markNotificationRead', { id: n.id });
    setItems((cur) => cur.filter((it) => it.id !== n.id));
    setBusyId(null);
  };

  const count = items.length;

  return (
    <>
      <button
        style={bellBtnStyle}
        onClick={() => { setOpen((o) => !o); if (!open) refresh(); }}
        title="Notifications [N]"
      >
        <FiBell size={16} />
        {count > 0 && <span style={badgeStyle}>{count}</span>}
      </button>

      <div
        style={drawerOverlayStyle(open, visible)}
        onClick={() => setOpen(false)}
      >
        <div style={drawerStyle(visible)} onClick={(e) => e.stopPropagation()}>
          <div style={drawerHeaderStyle}>
            <span style={drawerTitleStyle}>NOTIFICATIONS</span>
            <button style={closeBtnStyle} onClick={() => setOpen(false)} title="Close [ESC]">
              <FiX size={14} />
            </button>
          </div>

          <div style={scrollBodyStyle}>
            <div style={scrollInnerStyle}>
              {error && (
                <div style={{ color: PALETTE.RED, fontSize: 11, padding: '6px 10px', border: `1px solid ${PALETTE.RED}55`, borderRadius: 4 }}>
                  {error}
                </div>
              )}
              {count === 0 && <div style={emptyStyle}>Nothing pending.</div>}
              {items.map((n) => (
                <div key={n.id} style={itemStyle}>
                  <div style={{ fontSize: 12, color: PALETTE.LIGHT, marginBottom: 4, fontWeight: 600 }}>{title(n)}</div>
                  <div style={{ fontSize: 11, color: PALETTE.DIM, marginBottom: 8, lineHeight: 1.4 }}>{body(n)}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {isInfoOnly(n) ? (
                      <button disabled={busyId === n.id} style={acceptBtn} onClick={() => dismiss(n)}>Dismiss</button>
                    ) : (
                      <>
                        <button disabled={busyId === n.id} style={acceptBtn} onClick={() => act(n, 'accept')}>Accept</button>
                        <button disabled={busyId === n.id} style={rejectBtn} onClick={() => act(n, 'reject')}>Reject</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function title(n: NotificationDTO): string {
  const p = n.payload as any;
  if (n.type === 'corp_contract') return `Corporation invite: ${p.corpName} [${p.corpTag}]`;
  if (n.type === 'corp_application') return `Membership request: ${p.corpName} [${p.corpTag}]`;
  if (n.type === 'drone_levelup') {
    const type = String(p.droneType ?? 'Drone');
    return `${type.charAt(0).toUpperCase()}${type.slice(1)} drone leveled up to L${p.level}`;
  }
  return `Asset transfer offer`;
}

function body(n: NotificationDTO): string {
  const p = n.payload as any;
  if (n.type === 'corp_contract') {
    return `${p.founderName} invites you to co-own this corporation. Accepting debits your share of the capital.`;
  }
  if (n.type === 'corp_application') {
    return `${p.applicantName} wants to join your corporation. Accept to add them as a member.`;
  }
  if (n.type === 'drone_levelup') {
    return `Hull, shield, and type-specific stats just got rebuilt at the new level. Check the bay for the updated XP bar.`;
  }
  return `${p.fromName} offers you "${p.assetName}". Accepting transfers ownership to you.`;
}

/** Info-only notifications (no Accept/Reject action — just Dismiss). */
function isInfoOnly(n: NotificationDTO): boolean {
  return n.type === 'drone_levelup';
}

function friendlyError(err: string | undefined): string {
  switch (err) {
    case 'insufficient_funds': return 'You cannot afford your capital contribution.';
    case 'invariant_one_per_constellation': return 'You already own a station in that constellation.';
    case 'expired': return 'That offer has expired.';
    case 'not_authorized': return 'You are not authorized to accept this.';
    default: return err ?? 'Action failed.';
  }
}

// ===== STYLES =====

const bellBtnStyle: React.CSSProperties = {
  // Stacked above the corp button at the bottom-center, just right of the
  // HULL/HEAT bars.
  position: 'absolute',
  bottom: 60,
  left: 'calc(50% + 168px)',
  zIndex: 50,
  pointerEvents: 'auto',
  width: 34,
  height: 34,
  borderRadius: 6,
  background: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  color: PALETTE.TEAL,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'JetBrains Mono', monospace",
};

const badgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -6,
  right: -6,
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  borderRadius: 8,
  background: PALETTE.ORANGE,
  color: PALETTE.DARK,
  fontSize: 10,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Right-side slide-in drawer, matching ShipInventoryModal / CorporationHUD.
const drawerOverlayStyle = (mounted: boolean, visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: visible ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
  zIndex: 100,
  pointerEvents: mounted ? 'auto' : 'none',
  transition: 'background-color 0.3s ease',
});

const drawerStyle = (visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '704px',
  maxWidth: '95vw',
  height: '100%',
  backgroundColor: 'rgba(15, 27, 38, 0.92)',
  borderLeft: `2px solid ${PALETTE.TEAL}`,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  boxShadow: `-4px 0 40px ${PALETTE.TEAL}22`,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
  transform: visible ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.3s ease',
});

const drawerHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const drawerTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: PALETTE.LIGHT,
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: PALETTE.DIM,
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
};

const scrollBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
};

const scrollInnerStyle: React.CSSProperties = {
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: '100%',
};

const emptyStyle: React.CSSProperties = {
  fontSize: 12,
  color: PALETTE.DIM,
  padding: 32,
  textAlign: 'center',
};

const itemStyle: React.CSSProperties = {
  padding: '12px',
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: 6,
  background: PALETTE.PANEL,
};

const acceptBtn: React.CSSProperties = {
  flex: 1,
  padding: '6px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  background: 'transparent',
  color: PALETTE.GREEN,
  border: `1px solid ${PALETTE.GREEN}`,
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
};

const rejectBtn: React.CSSProperties = {
  flex: 1,
  padding: '6px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  background: 'transparent',
  color: PALETTE.DIM,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
};

export default NotificationBell;
